import {
  Alert,
  Badge,
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Tag } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import type {
  Allergen,
  AllergenFormInput,
  DietaryTag,
  DietaryTagFormInput,
  MenuPosition,
  MenuPositionFormInput,
  ServiceType,
  ServiceTypeFormInput,
} from '../../menus/api';
import {
  createAllergen,
  createDietaryTag,
  createMenuPosition,
  createServiceType,
  listAllergens,
  listDietaryTags,
  listMenuPositions,
  listServiceTypes,
  updateAllergen,
  updateDietaryTag,
  updateMenuPosition,
  updateServiceType,
} from '../../menus/api';
import {
  type CategoryEntity,
  mapCategoryError,
  toAllergenCreateInput,
  toAllergenFormValues,
  toAllergenUpdateInput,
  toDietaryTagCreateInput,
  toDietaryTagFormValues,
  toDietaryTagUpdateInput,
  toMenuPositionCreateInput,
  toMenuPositionFormValues,
  toMenuPositionUpdateInput,
  toServiceTypeCreateInput,
  toServiceTypeFormValues,
  toServiceTypeUpdateInput,
} from '../../menus/category-form-schema';
import { AllergenForm } from './components/allergen-form';
import { DietaryTagForm } from './components/dietary-tag-form';
import { MenuPositionForm } from './components/menu-position-form';
import { ServiceTypeForm } from './components/service-type-form';

/**
 * `menu-categories/spec.md` → "Pantalla de categorías en back-office". No
 * permission-check helper exists in `apps/web-admin` (see apply-progress.md
 * deviation): the forms are always rendered and the API's 403 is the
 * authority, same criterion `mapCategoryError`/`mapSiteError` already apply
 * to every other action in this codebase that requires a permission.
 */
export function CategoriesPage(): JSX.Element {
  return (
    <section className="flex flex-col gap-8">
      <PageHeader
        icon={Tag}
        title="Categorías de catálogo"
        description="Administrá los tipos de servicio, etiquetas dietarias y alérgenos del catálogo."
      />

      <ServiceTypesSection />
      <PositionsSection />
      <DietaryTagsSection />
      <AllergensSection />
    </section>
  );
}

type ServiceTypeModalState = { mode: 'create' } | { mode: 'edit'; serviceType: ServiceType } | null;

function ServiceTypesSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canManage = useCanManageCategories();
  const { toast } = useToast();
  const [modal, setModal] = useState<ServiceTypeModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const query = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['service-types'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: ServiceTypeFormInput) => createServiceType(input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Tipo de servicio creado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ReturnType<typeof toServiceTypeUpdateInput>;
    }) => updateServiceType(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Tipo de servicio actualizado');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateServiceType(id, { isActive }),
    onMutate: () => setActionError(undefined),
    onSuccess: () => invalidate(),
    onError: (error) => setActionError(mapCategoryError(error, 'SERVICE_TYPE')),
  });

  if (query.isPending) {
    return <SectionSkeleton />;
  }

  if (query.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar los tipos de servicio">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const serviceTypes = query.data;

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Tipos de servicio</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nuevo tipo de servicio
          </Button>
        ) : null}
      </div>

      <CategoryTable
        entityLabel="tipo de servicio"
        rows={serviceTypes.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.name,
          displayOrder: serviceType.displayOrder,
          isActive: serviceType.isActive,
          extraCells: [],
          onEdit: () => setModal({ mode: 'edit', serviceType }),
          onToggleActive: () =>
            toggleActiveMutation.mutate({ id: serviceType.id, isActive: !serviceType.isActive }),
          togglingActive:
            toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === serviceType.id,
        }))}
      />

      {modal !== null ? (
        <ServiceTypeForm
          title={modal.mode === 'create' ? 'Nuevo tipo de servicio' : 'Editar tipo de servicio'}
          submitLabel={modal.mode === 'create' ? 'Crear tipo de servicio' : 'Guardar cambios'}
          mode={modal.mode}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...maybeFormError(
            mapCategoryErrorIfAny(
              modal.mode === 'create' ? createMutation.error : updateMutation.error,
              'SERVICE_TYPE',
            ),
          )}
          {...(modal.mode === 'edit'
            ? { defaultValues: toServiceTypeFormValues(modal.serviceType) }
            : {})}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation
                .mutateAsync(toServiceTypeCreateInput(values))
                .catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({
                id: modal.serviceType.id,
                input: toServiceTypeUpdateInput(modal.serviceType, values),
              })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

type PositionModalState = { mode: 'create' } | { mode: 'edit'; position: MenuPosition } | null;

/** `menu-positions/spec.md` → "Pantalla de posiciones en back-office": ABM junto a tipos de servicio, mismo permiso `menu.category.manage`. */
function PositionsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canManage = useCanManageCategories();
  const { toast } = useToast();
  const [modal, setModal] = useState<PositionModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const positionsQuery = useQuery({ queryKey: ['menu-positions'], queryFn: listMenuPositions });
  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['menu-positions'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: MenuPositionFormInput) => createMenuPosition(input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Posición creada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ReturnType<typeof toMenuPositionUpdateInput>;
    }) => updateMenuPosition(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Posición actualizada');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateMenuPosition(id, { isActive }),
    onMutate: () => setActionError(undefined),
    onSuccess: () => invalidate(),
    onError: (error) => setActionError(mapCategoryError(error, 'MENU_POSITION')),
  });

  if (positionsQuery.isPending || serviceTypesQuery.isPending) {
    return <SectionSkeleton />;
  }

  if (positionsQuery.isError || serviceTypesQuery.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar las posiciones del menú">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void positionsQuery.refetch();
              void serviceTypesQuery.refetch();
            }}
          >
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const positions = positionsQuery.data;
  const serviceTypes = serviceTypesQuery.data;
  const serviceTypeNames = Object.fromEntries(
    serviceTypes.map((serviceType) => [serviceType.id, serviceType.name]),
  );

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Posiciones del menú</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nueva posición
          </Button>
        ) : null}
      </div>

      <CategoryTable
        entityLabel="posición"
        extraColumns={['Tipo de servicio']}
        rows={positions.map((position) => ({
          id: position.id,
          name: position.name,
          displayOrder: position.displayOrder,
          isActive: position.isActive,
          extraCells: [serviceTypeNames[position.serviceTypeId] ?? position.serviceTypeId],
          onEdit: () => setModal({ mode: 'edit', position }),
          onToggleActive: () =>
            toggleActiveMutation.mutate({ id: position.id, isActive: !position.isActive }),
          togglingActive:
            toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === position.id,
        }))}
      />

      {modal !== null ? (
        <MenuPositionForm
          title={modal.mode === 'create' ? 'Nueva posición' : 'Editar posición'}
          submitLabel={modal.mode === 'create' ? 'Crear posición' : 'Guardar cambios'}
          mode={modal.mode}
          serviceTypes={serviceTypes}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...maybeFormError(
            mapCategoryErrorIfAny(
              modal.mode === 'create' ? createMutation.error : updateMutation.error,
              'MENU_POSITION',
            ),
          )}
          {...(modal.mode === 'edit'
            ? { defaultValues: toMenuPositionFormValues(modal.position) }
            : {})}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation
                .mutateAsync(toMenuPositionCreateInput(values))
                .catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({
                id: modal.position.id,
                input: toMenuPositionUpdateInput(modal.position, values),
              })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

type DietaryTagModalState = { mode: 'create' } | { mode: 'edit'; dietaryTag: DietaryTag } | null;

function DietaryTagsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canManage = useCanManageCategories();
  const { toast } = useToast();
  const [modal, setModal] = useState<DietaryTagModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const query = useQuery({ queryKey: ['dietary-tags'], queryFn: listDietaryTags });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['dietary-tags'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: DietaryTagFormInput) => createDietaryTag(input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Etiqueta dietaria creada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: ReturnType<typeof toDietaryTagUpdateInput>;
    }) => updateDietaryTag(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Etiqueta dietaria actualizada');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateDietaryTag(id, { isActive }),
    onMutate: () => setActionError(undefined),
    onSuccess: () => invalidate(),
    onError: (error) => setActionError(mapCategoryError(error, 'DIETARY_TAG')),
  });

  if (query.isPending) {
    return <SectionSkeleton />;
  }

  if (query.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar las etiquetas dietarias">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const dietaryTags = query.data;

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Etiquetas dietarias</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nueva etiqueta dietaria
          </Button>
        ) : null}
      </div>

      <CategoryTable
        entityLabel="etiqueta dietaria"
        extraColumns={['Color', 'Ícono']}
        rows={dietaryTags.map((dietaryTag) => ({
          id: dietaryTag.id,
          name: dietaryTag.name,
          displayOrder: dietaryTag.displayOrder,
          isActive: dietaryTag.isActive,
          extraCells: [dietaryTag.color ?? '—', dietaryTag.icon ?? '—'],
          onEdit: () => setModal({ mode: 'edit', dietaryTag }),
          onToggleActive: () =>
            toggleActiveMutation.mutate({ id: dietaryTag.id, isActive: !dietaryTag.isActive }),
          togglingActive:
            toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === dietaryTag.id,
        }))}
      />

      {modal !== null ? (
        <DietaryTagForm
          title={modal.mode === 'create' ? 'Nueva etiqueta dietaria' : 'Editar etiqueta dietaria'}
          submitLabel={modal.mode === 'create' ? 'Crear etiqueta dietaria' : 'Guardar cambios'}
          mode={modal.mode}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...maybeFormError(
            mapCategoryErrorIfAny(
              modal.mode === 'create' ? createMutation.error : updateMutation.error,
              'DIETARY_TAG',
            ),
          )}
          {...(modal.mode === 'edit'
            ? { defaultValues: toDietaryTagFormValues(modal.dietaryTag) }
            : {})}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation
                .mutateAsync(toDietaryTagCreateInput(values))
                .catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({
                id: modal.dietaryTag.id,
                input: toDietaryTagUpdateInput(modal.dietaryTag, values),
              })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

type AllergenModalState = { mode: 'create' } | { mode: 'edit'; allergen: Allergen } | null;

function AllergensSection(): JSX.Element {
  const queryClient = useQueryClient();
  const canManage = useCanManageCategories();
  const { toast } = useToast();
  const [modal, setModal] = useState<AllergenModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const query = useQuery({ queryKey: ['allergens'], queryFn: listAllergens });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['allergens'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: AllergenFormInput) => createAllergen(input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Alérgeno creado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReturnType<typeof toAllergenUpdateInput> }) =>
      updateAllergen(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Alérgeno actualizado');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateAllergen(id, { isActive }),
    onMutate: () => setActionError(undefined),
    onSuccess: () => invalidate(),
    onError: (error) => setActionError(mapCategoryError(error, 'ALLERGEN')),
  });

  if (query.isPending) {
    return <SectionSkeleton />;
  }

  if (query.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar los alérgenos">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const allergens = query.data;

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Alérgenos</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nuevo alérgeno
          </Button>
        ) : null}
      </div>

      <CategoryTable
        entityLabel="alérgeno"
        extraColumns={['Ícono']}
        rows={allergens.map((allergen) => ({
          id: allergen.id,
          name: allergen.name,
          displayOrder: allergen.displayOrder,
          isActive: allergen.isActive,
          extraCells: [allergen.icon ?? '—'],
          onEdit: () => setModal({ mode: 'edit', allergen }),
          onToggleActive: () =>
            toggleActiveMutation.mutate({ id: allergen.id, isActive: !allergen.isActive }),
          togglingActive:
            toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === allergen.id,
        }))}
      />

      {modal !== null ? (
        <AllergenForm
          title={modal.mode === 'create' ? 'Nuevo alérgeno' : 'Editar alérgeno'}
          submitLabel={modal.mode === 'create' ? 'Crear alérgeno' : 'Guardar cambios'}
          mode={modal.mode}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...maybeFormError(
            mapCategoryErrorIfAny(
              modal.mode === 'create' ? createMutation.error : updateMutation.error,
              'ALLERGEN',
            ),
          )}
          {...(modal.mode === 'edit'
            ? { defaultValues: toAllergenFormValues(modal.allergen) }
            : {})}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation
                .mutateAsync(toAllergenCreateInput(values))
                .catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({
                id: modal.allergen.id,
                input: toAllergenUpdateInput(modal.allergen, values),
              })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

function useCanManageCategories(): boolean {
  const currentUser = useCurrentUser();

  return hasPermission(currentUser.data, 'menu.category.manage');
}

function SectionSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

interface CategoryRow {
  id: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  extraCells: string[];
  onEdit: () => void;
  onToggleActive: () => void;
  togglingActive: boolean;
}

interface CategoryTableProps {
  entityLabel: string;
  extraColumns?: string[];
  rows: CategoryRow[];
}

function CategoryTable({ entityLabel, extraColumns = [], rows }: CategoryTableProps): JSX.Element {
  const canManage = useCanManageCategories();

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Tag}
        title="Todavía no hay categorías cargadas"
        description={`Creá la primera entrada de ${entityLabel} para empezar.`}
      />
    );
  }

  return (
    <Table>
      <caption className="sr-only">Listado de {entityLabel}</caption>
      <thead>
        <tr>
          <TableHeader>Nombre</TableHeader>
          {extraColumns.map((column) => (
            <TableHeader key={column}>{column}</TableHeader>
          ))}
          <TableHeader>Orden</TableHeader>
          <TableHeader>Estado</TableHeader>
          <TableHeader>Acciones</TableHeader>
        </tr>
      </thead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium text-slate-900">{row.name}</TableCell>
            {row.extraCells.map((cell, index) => (
              <TableCell key={index}>{cell}</TableCell>
            ))}
            <TableCell>{row.displayOrder}</TableCell>
            <TableCell>
              <Badge variant={row.isActive ? 'success' : 'neutral'}>
                {row.isActive ? 'Activo' : 'Inactivo'}
              </Badge>
            </TableCell>
            <TableCell>
              {canManage ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Pencil}
                  aria-label={`Editar ${entityLabel} ${row.name}`}
                  onClick={row.onEdit}
                >
                  Editar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={row.togglingActive}
                  aria-label={`${row.isActive ? 'Desactivar' : 'Activar'} ${entityLabel} ${row.name}`}
                  onClick={row.onToggleActive}
                >
                  {row.isActive ? 'Desactivar' : 'Activar'}
                </Button>
              </div>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function mapCategoryErrorIfAny(error: unknown, entity: CategoryEntity): string | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }

  return mapCategoryError(error, entity);
}

/**
 * `exactOptionalPropertyTypes` treats an explicit `formError={undefined}` as
 * a different (rejected) shape than the prop being absent; this omits the
 * key entirely instead of ever assigning `undefined` to it (same pattern as
 * `menu-form-page.tsx`'s `maybeError`).
 */
function maybeFormError(
  message: string | undefined,
): { formError: string } | Record<string, never> {
  return message === undefined ? {} : { formError: message };
}
